import MutableTrack from './MutableTrack.js';
import Preset from 'repos/Preset.js';
import Tone from 'tone';
import ethereum from 'decentralized/ethereum.js';
import ParameterContext from 'effects/ParameterContext.js';
import {INSTRUMENT_RACK} from 'constants/track-types.js';
import {SAMPLED_INSTRUMENT} from 'constants/instrument-types.js';
import InstrumentRackInstrument from './InstrumentRackInstrument.js';
import {constructTrack} from 'track/constructTrack.js';
import {setTrackIsLoading} from 'actions/index.js';

const presets = ethereum.presets;

class InstrumentRackTrack extends MutableTrack {
    constructor(
        trackNumber,
        sequencerEngine,
        mixSession,
        numberOfPatterns,
        update,
        subSequence,
        createSynth=true) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
        
        this.setupEffects();

        this.tracks = [];

        this.name = 'Instrument Rack';

        this.preset = new Preset();
        this.preset.track = this;
    }

    async addPreset(preset) {
        let presetName = preset.title;
        let data = await presets.getPresetData(preset);
        let track = await this.sequencerEngine.loadTrackPreset(data, undefined, true, false, presetName, undefined, preset);
        track.presetName = presetName;
       
        let instrumentRackInstrument = new InstrumentRackInstrument(track, this);

        setTimeout(() => {
            track.loadLostParameters();
        }, [750]);
        return this.addInstrument(instrumentRackInstrument);
    }

    async addSample(sample, samplesStream) {
        let track = this.addInstrumentType(SAMPLED_INSTRUMENT);
        track.track.sample = sample;
        await track.track.instrument.newSample(0, sample, samplesStream);
        if (samplesStream) {
            track.track.samplesStream = samplesStream;
            track.track.samplesStream.initBuffers();
        }
        track.track.presetName = sample.name;
        return track;
    }

    getSubDevices() {
        return this.tracks.map(
            track => ({value: track, label: track.presetName || track.getName()}));
    }

    getSubDeviceIndex(subDevice) {
        return this.tracks.indexOf(subDevice);
    }

    getSubDevice(index) {
        return this.tracks[index];
    }

    getSamplesStream() {
        let selectedTrack = this.getSelectedTrack();
        if (selectedTrack) {
            return selectedTrack.track.samplesStream;
        }
        return undefined;
    }

    getSelectedTrack() {
        return this.sequencerEngine.state.tracks.selectedRackSubTrack[this.id];
    }

    loadTrackInfo(x, sample) {
        // check which instrument is selected...
        let selectedTrack = this.getSelectedTrack();
        if (selectedTrack) {
            selectedTrack.track.loadTrackInfo(x, sample);
            this.tracks = [... this.tracks];
        } else {
            this.addSample(sample);
            this.tracks = [... this.tracks];
        }

        this.updateSteps();
        // if none then add a new track within it
    }

    addInstrumentType(type) {
        let track = constructTrack(
            this.sequencerEngine,
            'INSTRUMENT',
            type);

        track.setSends(this.sequencerEngine.sends);
        track.effectsChains[0].clear();

        let instrumentRackInstrument = new InstrumentRackInstrument(track, this);
        return this.addInstrument(instrumentRackInstrument);
    }

    getName() {
        return 'Instrument Rack';
    }

    getTrackType() {
        return INSTRUMENT_RACK;
    }

    getInstrumentType() {
        return INSTRUMENT_RACK;
    }

    get instrument() {
        return this;
    }

    get inputNode() {
        return this.gainNode;
    }

    addInstrument(instrumentRackInstrument) {
        let {track} = instrumentRackInstrument;
        track.trackNumber = this.tracks.length;
        this.tracks = [... this.tracks, instrumentRackInstrument];
        track.muteableNode.connect(this.gainNode);
        track.group = this;
        return instrumentRackInstrument;
    }

    deleteInstrument(track) {
        let index = this.tracks.indexOf(track);
        if (index === -1) {
            return;
        }
        this.tracks.splice(index, 1);
        track.track.dispose();
    }

    dispose() {
        this.tracks.forEach(
            t => t.dispose());
        this.tracks = undefined;
        super.dispose(false);
    }

    liveTrigger(stepData, sliceNumber=0) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.midiEffects.liveInputTrigger(stepData));
    }

    releaseAll(time) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.releaseAll(time));
    }

    liveTriggerRelease(transpose, sliceNumber) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.midiEffects.liveInputRelease(transpose, sliceNumber));
    }

    // functions for triggering the underlying Samplers of the track
    trigger(stepData, time, actualStepNumber) {
        if (this.isFrozen) {
            super.trigger(stepData, time, actualStepNumber);
            return;
        }

        this.tracks.forEach(
            ({track}) => {
                track.midiEffects.input(stepData, time, actualStepNumber);
            });

        this.onStepEnded(time, actualStepNumber);
    }

    triggerSamples(stepData, time) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.triggerSamples(stepData, time));
    }

    triggerSlice(slice) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.triggerSlice(slice));
    }

    triggerSample(slice, transpose, skipRelease=false) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.triggerSample(slice, transpose, skipRelease));
    }

    triggerRelease(transpose, sliceNumber=0) {
        if (this.isFrozen) {
            return;
        }
        this.tracks.forEach(({track}) => track.triggerRelease(transpose, sliceNumber));
    }

    getJson(skipFreeze) {
        let json = super.getJson(skipFreeze);
        json.tracks = this.tracks.map(t => t.getJson(skipFreeze));
        return json;
    }

    onBPMUpdated() {
        this.effectsChains.forEach(chain => chain.onBPMUpdated());
        for (let i in this.tracks) {
            for (let j in this.tracks[i].effectsChains) {
                this.tracks[i].effectsChains[j].onBPMUpdated();
            }
        }
    }

    async fromJson(json) {
        if (this._loaded) {
            return {};
        }
        
        super.fromJson(json);

        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, true));

        let bulk = {};
        for (let i in json.tracks) {
            let {type, instrumentType} = json.tracks[i].track;
            let track = constructTrack(this.sequencerEngine, type, instrumentType);
            track.setSends(this.sequencerEngine.sends);
            let instrumentRackInstrument = new InstrumentRackInstrument(track, this);
            bulk = {
                ... bulk,
                ... await instrumentRackInstrument.fromJson(json.tracks[i], false)
            };
            this.addInstrument(instrumentRackInstrument);
        }
        this.store.dispatch(setTrackIsLoading(this.subSequence.id, this.trackNumber, false));
        this._loaded = true;
        return bulk;
    }

    onNewStep(step) {
        if (this.isFrozen) {
            super.onNewStep();
            return;
        }
        super.onNewStep();
        this.tracks.forEach(({track}) => track.onNewStep(step));
    }

    onStepEnded(time, step) {
        if (this.isFrozen) {
            super.onNewStep();
            return;
        }

        for (let i in this.tracks) {
            let {track} = this.tracks[i];
            let triggers = track.midiEffects.output(time, step, step);
            triggers.forEach(
                trigger => track.trigger(trigger.stepData, trigger.time, step));
        }
    }

    start() {
        super.start();
        if (this.isFrozen) {
            return;
        }

        this.tracks.forEach(({track}) => track.start());
    }

    solo(solo) {
        super.solo(solo);
        this.tracks.forEach(({track}) => track.solo(solo, undefined, false));
    }

    getCopy(constr, sequencerEngine, ignoreId=true) {
        let rack = super.getCopy(
            (a,b,c,d,e,f) => new InstrumentRackTrack(a,b,c,d,e,f),
            sequencerEngine,
            ignoreId);
        for (let i in this.tracks) {
            let trackToCopy = this.tracks[i].track;
            let copiedTrack = trackToCopy.getCopy(undefined, sequencerEngine, ignoreId);
            let subInst = new InstrumentRackInstrument(copiedTrack, rack);

            // copy over the effects
            copiedTrack.effectsChains[0].dispose();
            copiedTrack.effectsChains = trackToCopy.effectsChains.map(
                chain => chain.getCopy(copiedTrack, sequencerEngine, ignoreId));
            copiedTrack.effectsChains[0].updateEffects();

            // copy the volume
            subInst.volumeParameter.copyFrom(this.tracks[i].volumeParameter);

            // add it to the instrument rack
            rack.addInstrument(subInst);

        }
        return rack;
    }

    getAllParameters() {
        let parameters = super.getAllParameters();
        if (this.isFrozen) {
            return parameters;
        }

        for (let i in this.tracks) {
            parameters = [
                ... parameters,
                ... this.tracks[i].getAllParameters()];
        }
        return parameters;
    }

    async makePublicIfNeeded() {
        for (let i in this.tracks) {
            await this.tracks[i].track.makePublicIfNeeded();
        }
    }

    unfreezeInstrument(json=this.frozenJson) {
        let i=0;
        for (let track of json.trackParameters.tracks) {
            this.tracks[i].track.createNewInstrument();
            this.tracks[i].track.instrument.fromJson(track.track.instrument);
            i++;
        }
    }
};

export default InstrumentRackTrack;
